import { hostname } from 'node:os';

import { pino } from 'pino';
import type { Logger, LoggerOptions, TransportSingleOptions } from 'pino';

import { env } from '@fim/config';

/**
 * Fields that must never be written to logs in plaintext. The list covers the
 * most common locations where credentials, tokens, and other secrets leak into
 * structured log payloads (HTTP headers, auth bodies, cookies, etc.).
 *
 * pino's `redact` option supports both dotted and bracketed JSON paths; we
 * enumerate the realistic nesting depths used across the FIM services so that a
 * secret is scrubbed regardless of where it appears in the merged log object.
 */
const REDACT_PATHS: readonly string[] = [
  'password',
  'newPassword',
  'currentPassword',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'authorization',
  'cookie',
  'set-cookie',
  // Nested under a request object.
  'req.headers.authorization',
  'req.headers.cookie',
  'req.body.password',
  'req.body.token',
  'req.body.accessToken',
  'req.body.refreshToken',
  // Nested under a response object.
  'res.headers["set-cookie"]',
  // Generic header maps (case variations seen across HTTP stacks).
  'headers.authorization',
  'headers.Authorization',
  'headers.cookie',
  'headers.Cookie',
  '*.password',
  '*.token',
  '*.accessToken',
  '*.refreshToken',
];

/** Placeholder rendered in place of a redacted value. */
const REDACT_CENSOR = '[REDACTED]';

/** The (non-secret) trio of env values the logger actually needs. */
interface LoggerEnv {
  nodeEnv: 'development' | 'test' | 'production';
  logLevel: string;
  serviceName: string;
}

/** Pino levels accepted by the schema; mirrored for the raw fallback below. */
const LOG_LEVELS: readonly string[] = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'];

/**
 * Resolve NODE_ENV / LOG_LEVEL / SERVICE_NAME. Prefer the validated env, but a
 * logger must never crash the process over an UNRELATED misconfigured secret —
 * e.g. during a Next.js production build of the marketing site, where reading
 * `@fim/config`'s (lazy) `env` throws because no server secrets exist. In that
 * case degrade to raw `process.env` with the schema's own defaults.
 */
function readLoggerEnv(): LoggerEnv {
  try {
    return {
      nodeEnv: env.NODE_ENV,
      logLevel: env.LOG_LEVEL,
      serviceName: env.SERVICE_NAME ?? 'fim',
    };
  } catch {
    const raw = typeof process !== 'undefined' ? process.env : ({} as NodeJS.ProcessEnv);
    const nodeEnv =
      raw.NODE_ENV === 'production' || raw.NODE_ENV === 'test' ? raw.NODE_ENV : 'development';
    const rawLevel = raw.LOG_LEVEL?.trim() ?? '';
    return {
      nodeEnv,
      logLevel: LOG_LEVELS.includes(rawLevel) ? rawLevel : 'info',
      serviceName: raw.SERVICE_NAME?.trim() || 'fim',
    };
  }
}

const loggerEnv = readLoggerEnv();

const isProduction = loggerEnv.nodeEnv === 'production';
const isTest = loggerEnv.nodeEnv === 'test';

/**
 * In development we stream through `pino-pretty` for human-friendly, colorized
 * output. In production (and test) we emit newline-delimited JSON so logs can be
 * ingested by aggregators (Loki, Datadog, CloudWatch, etc.) without a parsing
 * step. The transport is only attached when pretty printing is desired.
 */
function buildTransport(): TransportSingleOptions | undefined {
  if (isProduction || isTest) {
    return undefined;
  }
  // Under Next.js (bundled server / SSR), pino's worker-thread transport cannot
  // resolve its worker file and crashes the request. Fall back to plain JSON.
  if (process.env.NEXT_RUNTIME) {
    return undefined;
  }

  return {
    target: 'pino-pretty',
    options: {
      colorize: true,
      translateTime: 'SYS:yyyy-mm-dd HH:MM:ss.l',
      ignore: 'pid,hostname',
      singleLine: false,
      messageKey: 'msg',
      errorLikeObjectKeys: ['err', 'error'],
    },
  };
}

/**
 * Base configuration shared by the root logger and every child created from it.
 * Exported so that sibling helpers (e.g. the HTTP request logger) can derive a
 * consistent configuration instead of re-declaring serializers/redaction.
 */
export const baseLoggerOptions: LoggerOptions = {
  level: loggerEnv.logLevel,
  // ISO-8601 timestamps with millisecond precision, e.g. 2026-06-05T12:34:56.789Z
  timestamp: pino.stdTimeFunctions.isoTime,
  messageKey: 'msg',
  errorKey: 'err',
  // Surface human-readable level labels in the JSON output instead of numbers.
  formatters: {
    level(label: string, _number: number) {
      return { level: label };
    },
    bindings(bindings) {
      return {
        pid: bindings['pid'],
        hostname: bindings['hostname'],
      };
    },
  },
  redact: {
    paths: [...REDACT_PATHS],
    censor: REDACT_CENSOR,
    remove: false,
  },
  base: {
    service: loggerEnv.serviceName,
    env: loggerEnv.nodeEnv,
    hostname: hostname(),
  },
  // Silence logging entirely under test unless a level is explicitly requested,
  // keeping unit-test output clean and deterministic.
  enabled: !isTest || loggerEnv.logLevel !== 'silent',
};

/**
 * The application-wide root logger. Import this directly for module-level or
 * one-off logging, or call {@link createLogger} to derive a namespaced child.
 *
 * @example
 * ```ts
 * import { logger } from '@fim/logger';
 * logger.info({ userId }, 'user authenticated');
 * ```
 */
export const logger: Logger = (() => {
  const transport = buildTransport();
  return transport
    ? pino({ ...baseLoggerOptions, transport })
    : pino(baseLoggerOptions);
})();

export type { Logger };
